import React from 'react';
import { ShieldAlert } from 'lucide-react';

export interface NotOwnerCardProps {
  /** Short noun describing what the user is being blocked from, e.g. "church", "subscription", "billing history". */
  resource: string;
  /** Optional specific instance name, e.g. the church's display name. */
  resourceName?: string;
  /** Override the default heading. */
  title?: string;
  /** Override the default description. */
  description?: string;
  /** Override the default icon. */
  icon?: React.ReactNode;
  /** Optional call-to-action button. */
  action?: { label: string; onClick: () => void };
  /** Additional wrapper classes. */
  className?: string;
}

/**
 * Modular "access denied" card rendered when the current user is not an owner/admin
 * of the resource they're trying to view (typically the active church). Use whenever
 * an admin-app API call returns 401 or 403 and you want a friendly, branded empty
 * state instead of a generic error banner.
 */
export const NotOwnerCard: React.FC<NotOwnerCardProps> = ({
  resource,
  resourceName,
  title,
  description,
  icon,
  action,
  className,
}) => {
  const heading = title ?? `You are not an owner of this ${resource}`;
  const detail =
    description ??
    (resourceName
      ? `Only owners and admins of "${resourceName}" can view this ${resource}. Ask a church admin to grant you access.`
      : `Only owners and admins can view this ${resource}. Ask a church admin to grant you access.`);

  return (
    <div
      role="alert"
      className={`max-w-2xl mx-auto text-center p-8 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl ${className ?? ''}`}
    >
      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-800/50">
        {icon ?? <ShieldAlert className="h-6 w-6 text-amber-600 dark:text-amber-300" />}
      </div>
      <h2 className="text-lg font-semibold text-amber-900 dark:text-amber-100">{heading}</h2>
      <p className="mt-2 text-sm text-amber-800 dark:text-amber-200">{detail}</p>
      {action && (
        <button
          onClick={action.onClick}
          className="mt-4 inline-flex items-center px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium transition-colors"
        >
          {action.label}
        </button>
      )}
    </div>
  );
};

/** Returns true when an axios/fetch error represents an authorization failure (401 or 403). */
export function isForbiddenError(err: unknown): boolean {
  const status = (err as { response?: { status?: number }; status?: number } | null | undefined)?.response?.status
    ?? (err as { status?: number } | null | undefined)?.status;
  return status === 401 || status === 403;
}

export default NotOwnerCard;
